
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import FloatingButtons from '../../components/feature/FloatingButtons';
import ReservationForm from '../../components/feature/ReservationForm';
import { updateSEO, addStructuredData } from '../../utils/seo';

export default function Carpas() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    
    // Update SEO
    updateSEO({
      title: 'Zona de Carpas - Castilletes Parque Tayrona | Camping y Glamping',
      description: 'Vive la aventura bajo las estrellas en nuestra zona de carpas. Desde carpas individuales hasta familiares. Experiencia de camping auténtica en el Parque Tayrona.',
      keywords: 'carpas Tayrona, camping Parque Tayrona, glamping Colombia, carpas familiares, aventura naturaleza',
      canonical: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/carpas`
    });

    // Add structured data
    addStructuredData({
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Zona de Carpas Castilletes",
      "description": "Experiencia de camping auténtica con carpas para 1 a 6 personas en el Parque Nacional Tayrona",
      "category": "Camping",
      "brand": {
        "@type": "Brand",
        "name": "Castilletes Parque Tayrona"
      },
      "offers": {
        "@type": "AggregateOffer",
        "availability": "https://schema.org/InStock",
        "priceCurrency": "COP"
      },
      "amenityFeature": [
        {
          "@type": "LocationFeatureSpecification",
          "name": "Desayuno incluido",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Baños compartidos",
          "value": true
        },
        {
          "@type": "LocationFeatureSpecification",
          "name": "Acceso a piscina",
          "value": true
        }
      ]
    });
  }, []);

  const tentTypes = [
    {
      name: 'Carpa Individual',
      capacity: '1 persona',
      features: [
        'Carpa compacta para viajeros solitarios',
        'Colchón individual confortable',
        'Ropa de cama incluida',
        'Espacio personal optimizado',
        'Ideal para mochileros',
        'Experiencia auténtica de camping'
      ],
      image:
        'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/8805110088e90b777c70cfa4e5d6fffd.png'
    },
    {
      name: 'Carpa Doble',
      capacity: '2 personas',
      features: [
        'Carpa resistente al clima tropical',
        'Colchón doble confortable',
        'Ropa de cama completa incluida',
        'Ventilación natural',
        'Acceso a baños compartidos'
      ],
      image:
        'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/8805110088e90b777c70cfa4e5d6fffd.png'
    },
    {
      name: 'Carpa Triple',
      capacity: '3 personas',
      features: [
        'Carpa espaciosa para tres personas',
        'Tres colchones individuales',
        'Ropa de cama para todos',
        'Mayor espacio interior',
        'Ideal para amigos o familia pequeña'
      ],
      image:
        'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/b7bdc93e81e8bee49993e4d3ca21efe6.jpeg'
    },
    {
      name: 'Carpa Cuádruple',
      capacity: '4 personas',
      features: [
        'Carpa familiar amplia',
        'Cuatro colchones cómodos',
        'Ropa de cama completa',
        'Perfecta para familias',
        'Mayor privacidad'
      ],
      image:
        'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/e4265f3732af2d13788165ac6d389a33.png'
    },
    {
      name: 'Carpa Familiar Grande',
      capacity: '5-6 personas',
      features: [
        'Carpa extra grande para grupos',
        'Múltiples colchones',
        'Ropa de cama para todos',
        'Ideal para grupos familiares',
        'Máximo confort y espacio'
      ],
      image:
        'https://static.readdy.ai/image/7a81e037875ba9699c319612a5c3de16/7cdd06b57ef42344c3c327916d7929a0.jpeg'
    }
  ];

  const campingFeatures = [
    { icon: 'ri-restaurant-line', name: 'Desayuno incluido' },
    { icon: 'ri-wifi-line', name: 'WiFi en área común' },
    { icon: 'ri-water-line', name: 'Acceso a piscina' },
    { icon: 'ri-shower-line', name: 'Baños y duchas' },
    { icon: 'ri-leaf-line', name: 'Experiencia ecológica' },
    { icon: 'ri-shield-check-line', name: 'Seguridad 24/7' }
  ];

  const activities = [
    {
      title: 'Senderismo Nocturno',
      description:
        'Explora la selva bajo las estrellas con guías expertos del parque',
      icon: 'ri-flashlight-line'
    },
    {
      title: 'Observación de Fauna',
      description:
        'Descubre monos, aves tropicales y la rica biodiversidad del Tayrona',
      icon: 'ri-eye-line'
    },
    {
      title: 'Fogata y Tradiciones',
      description:
        'Noches mágicas compartiendo historias y cultura local',
      icon: 'ri-fire-line'
    },
    {
      title: 'Yoga al Amanecer',
      description:
        'Conecta con la naturaleza en sesiones matutinas frente al mar',
      icon: 'ri-heart-pulse-line'
    },
    {
      title: 'Caminata a Playas',
      description:
        'Acceso directo a las playas vírgenes del Parque Tayrona',
      icon: 'ri-map-2-line'
    },
    {
      title: 'Talleres de Supervivencia',
      description:
        'Aprende técnicas básicas de supervivencia en la selva',
      icon: 'ri-compass-3-line'
    }
  ];

  // Helper function to safely navigate; prevents unhandled errors
  const safeNavigate = (path) => {
    try {
      navigate(path);
    } catch (error) {
      console.error('Navigation error:', error);
    }
  };

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section
        className="relative h-96 bg-cover bg-center"
        style={{
          backgroundImage: `url('https://public.readdy.ai/ai/img_res/67a17159d4dd2ebe783ff9a4335f9f29.jpg')`
        }}
      >
        <div className="absolute inset-0 bg-black/40"></div>
        <div className="relative container mx-auto px-4 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Zona de Carpas
            </h1>
            <p className="text-xl mb-6">
              Vive la aventura bajo las estrellas del Tayrona
            </p>
          </div>
        </div>
      </section>

      {/* Tent Types */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6">
              Opciones de Carpas Disponibles
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Desde aventura auténtica hasta glamping de lujo, encuentra tu
              experiencia perfecta en la naturaleza
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {tentTypes.map((tent, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={tent.image}
                    alt={tent.name}
                    className="w-full h-64 object-cover hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-semibold">
                    {tent.capacity}
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-gray-800 mb-2">
                    {tent.name}
                  </h3>
                  <p className="text-green-600 font-semibold mb-4">
                    Capacidad: {tent.capacity}
                  </p>

                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-800 mb-3">
                      Incluye:
                    </h4>
                    <ul className="space-y-2">
                      {tent.features.map((feature, idx) => (
                        <li
                          key={idx}
                          className="flex items-center text-gray-600"
                        >
                          <i className="ri-check-line text-green-600 mr-2"></i>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Servicios Incluidos */}
          <div className="bg-white rounded-xl p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Servicios Incluidos en Tu Estadía
            </h3>
            <div className="flex flex-wrap justify-center gap-8 max-w-4xl mx-auto">
              {campingFeatures.map((service, idx) => (
                <div key={idx} className="text-center w-32">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i
                      className={`${service.icon} text-2xl text-green-600`}
                    ></i>
                  </div>
                  <p className="text-sm text-gray-600 font-medium">
                    {service.name}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Safety & Guidelines */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-800 mb-8 text-center">
              Información Importante para Tu Estadía
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <i className="ri-leaf-line text-green-600 mr-2"></i>
                  Recomendaciones
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Traer repelente de insectos</li>
                  <li>• Ropa cómoda y cerrada</li>
                  <li>• Calzado antideslizante</li>
                  <li>• Protector solar</li>
                  <li>• Respetar la fauna local</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md">
                <h4 className="font-semibold text-gray-800 mb-3 flex items-center">
                  <i className="ri-information-line text-green-600 mr-2"></i>
                  Políticas
                </h4>
                <ul className="space-y-2 text-gray-600">
                  <li>• Check-in: 2:00 PM</li>
                  <li>• Check-out: 11:00 AM</li>
                  <li>• No se permiten mascotas</li>
                  <li>• Entrada al parque no incluida</li>
                  <li>• Parqueadero disponible</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Formulario de Reserva */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <ReservationForm />
        </div>
      </section>

      <Footer />
    </div>
  );
}
